import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import ToggleButton from 'react-bootstrap/ToggleButton';
import CustomTable from '../Table/CustomTable';
import Form from 'react-bootstrap/Form';
import moment from 'moment-timezone';
import ButtonLoading from '../Common/ui/ButtonLoading';
import SQCLineChart from '../SQCLineChart';
import SQCMovingRangeChart from '../SQCMovingRangeChart';
import SQCScatterPlot from '../SQCScatterPlot';
import SQCHistogram from '../SQCHistogram';

const ChartAssessment = ({
  showModal,
  setShowModal,
  currentLogo,
  resultsDataTableData,
  currentParameter,
  unit,
  summaryStatisticsAssessmentData,
  summaryStatisticsAssessmentColumns,
  modalAssessment,
  handleAssessmentChange,
  saveChartAssessment,
  currentMethod,
  currentInstrument,
  checkedCombinedInitialAssessment,
  processChartData,
  allParams,
  injectLimitsToData,
  tz,
  chartButtonText,
  modalMode
}) => {
  const [activeParam, setActiveParam] = useState(currentParameter);
  const [processedChartData, setProcessedChartData] = useState([]);
  const [processedChartScalars, setProcessedChartScalars] = useState(null);

  const normalizeSummaryStatisticsForTable = (data = []) => {
    return data.map((row) => {
      if (row.values) {
        return {
          statistic: row.statistic,
          ...row.values
        };
      }

      return row;
    });
  };

  useEffect(() => {
    if (currentParameter) {
      setActiveParam(currentParameter);
    }
  }, [currentParameter]);

  useEffect(() => {
    if (modalAssessment && typeof processChartData === 'function') {
      const rawData = modalAssessment.chartDataSnapshot?.[activeParam] || [];

      // Ensure rawData is always an array
      const validatedRawData = Array.isArray(rawData) ? rawData : [];

      const injectedData = injectLimitsToData(
        validatedRawData,
        modalAssessment.chartControlStrategySnapshot || {}
      );

      const { chartData, chartScalars } = processChartData(
        injectedData,
        modalAssessment.chartControlStrategySnapshot
      );

      setProcessedChartData(chartData);
      setProcessedChartScalars(chartScalars);
    }
  }, [modalAssessment, activeParam, processChartData, injectLimitsToData]);

  const headerModelInitialAssessment = checkedCombinedInitialAssessment
    ? modalAssessment.fullKey
      ? summaryStatisticsAssessmentColumns.filter((item) => {
          const headerKey = modalAssessment?.fullKey?.split('|')[4];
          return (
            item.headerName === headerKey ||
            item.headerName === 'Statistic' ||
            (item.headerName.includes(headerKey) && modalAssessment.archive)
          );
        })
      : summaryStatisticsAssessmentColumns
    : summaryStatisticsAssessmentColumns.filter(
        (item) => item.headerName === currentParameter || item.headerName === 'Statistic'
      );
  const normalizedSummaryData = normalizeSummaryStatisticsForTable(summaryStatisticsAssessmentData);
  return (
    <Modal
      size="xl"
      show={showModal}
      onHide={() => setShowModal(false)}
      dialogClassName="modal-90w"
    >
      <Modal.Header closeButton>
        <Modal.Title>Chart Assessment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col className="col-center">
            <img alt="logo" src={currentLogo} width="200" />
          </Col>
          <Col className="col-center">
            <h3>ASTM SQC Insights</h3>
          </Col>
        </Row>
        <Row className="report-header">
          <h4>Initial Assessment Report</h4>
        </Row>
        <Row className="ia-table">
          <Col className="col-center">Method:</Col>
          <Col className="col-center">{currentMethod}</Col>
          <Col className="col-center">Date:</Col>
          <Col className="col-center">
            {modalAssessment?.createdAt?.toDate
              ? moment(modalAssessment.createdAt.toDate())
                  .tz(tz || 'UTC')
                  .format('YYYY/MM/DD')
              : resultsDataTableData?.[0]?.date || ''}
          </Col>
        </Row>

        <Row className="ia-table">
          <Col className="col-center">Instrument:</Col>
          <Col className="col-center">{currentInstrument}</Col>
          <Col className="col-center">Assessment by:</Col>
          <Col className="col-center">{modalAssessment?.createdBy}</Col>
        </Row>
        <Row className="report-header">
          <h4>
            Summary Statistics: {currentParameter} {unit ? `, ${unit}` : ''}
          </h4>
        </Row>
        <Row className="statistics-table">
          <CustomTable
            hideFooter={true}
            data={normalizedSummaryData}
            header={headerModelInitialAssessment}
            numberOfRows={normalizedSummaryData.length < 50 ? normalizedSummaryData.length : 50}
            allowScroll={false}
          />
        </Row>

        {processedChartData.length > 0 &&
          modalAssessment?.chartLinesSnapshot?.[activeParam] &&
          processedChartScalars && (
            <>
              <Row className="report-header align-items-center">
                <Col>
                  <h4>Charts:</h4>
                </Col>
                {checkedCombinedInitialAssessment && allParams.length > 1 && (
                  <Col className="text-end">
                    <Form.Group
                      controlId="parameterSelect"
                      className="mb-3 d-flex align-items-center justify-content-end gap-2"
                    >
                      <Form.Label className="mb-0">Select Parameter:</Form.Label>
                      <Form.Control
                        as="select"
                        value={activeParam}
                        onChange={(e) => setActiveParam(e.target.value)}
                        style={{ width: '200px' }}
                      >
                        {allParams.map((param) => (
                          <option key={param} value={param}>
                            {param}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  </Col>
                )}
              </Row>

              <div className="chart-row-container">
                <SQCLineChart
                  xAxis="updatedIndex"
                  syncId="chained"
                  yMin={processedChartScalars.cYMin}
                  yMax={processedChartScalars.cYMax}
                  data={processedChartData}
                  lines={modalAssessment.chartLinesSnapshot?.[activeParam] || []}
                />
                <SQCMovingRangeChart
                  xAxis="updatedIndex"
                  data={processedChartData}
                  syncId="chained2"
                  yMin={0}
                  yMax={processedChartScalars.mrYMax}
                  lines={[
                    { yAxis: 'mrOmits', name: 'Data', color: '#32CD32' },
                    {
                      yAxis: 'mean',
                      overrideValue: processedChartScalars.mrMean,
                      name: 'Average',
                      reference: true,
                      color: '#FFBF00'
                    },
                    {
                      yAxis: 'sd3p',
                      overrideValue: processedChartScalars.mrUCL,
                      reference: true,
                      color: '#FBCEB1'
                    }
                  ]}
                />
              </div>
              <div>
                {processedChartScalars.histogramData ? (
                  <SQCHistogram data={processedChartScalars.histogramData} />
                ) : (
                  <div>No Histogram</div>
                )}
              </div>
              <div>
                <SQCScatterPlot
                  xAxis="zScoreOmits"
                  data={processedChartData}
                  syncId="chained2"
                  xMin={processedChartScalars.qqXMin}
                  xMax={processedChartScalars.qqXMax}
                  yMin={processedChartScalars.qqYMin}
                  yMax={processedChartScalars.qqYMax}
                  collections={[
                    {
                      name: 'Lab Results',
                      color: '#32CD32',
                      data: processedChartData
                        .filter(
                          (cd) => !isNaN(Number(cd.zScoreOmits)) && !isNaN(Number(cd.resultOmits))
                        )
                        .map((cd) => ({
                          x: Number(cd.zScoreOmits),
                          y: Number(cd.resultOmits),
                          fullKey: cd.fullKey,
                          parameter: cd.parameter
                        }))
                    },
                    {
                      name: 'Trend Line',
                      color: '#FFBF00',
                      data: processedChartScalars.trendData,
                      line: true
                    }
                  ]}
                />
              </div>
            </>
          )}

        {[
          {
            label: '1. Trend Patterns (upward or downward) Assessment',
            key: 'trendingDirectionEvident'
          },
          { label: '2. Clustering Patterns Assessment', key: 'clusteringEvident' },
          { label: '3. Cyclical Patterns Assessment', key: 'cyclicalPatterns' },
          {
            label: '4. Normal Distribution Assessment',
            key: 'normalDistribution'
          },
          { label: '5. TPI Assessment', key: 'tpiAssessment' }
        ].map(({ label, key }) => (
          <Row key={key} className="ia-table">
            <Col lg={7}>
              <h6 className="ia-table" style={{ marginLeft: '50px' }}>
                {label}
              </h6>
            </Col>
            <Col>
              {['Pass', 'Fail'].map((value) => (
                <ToggleButton
                  key={`${key}-${value}`}
                  id={`${key}-${value}`}
                  type="radio"
                  variant="outline-primary"
                  name={`radio-${key}`}
                  value={value}
                  style={
                    modalAssessment?.[key] === value
                      ? { backgroundColor: 'navy', color: 'white' }
                      : {}
                  }
                  checked={modalAssessment?.[key] === value}
                  onChange={(e) => handleAssessmentChange(e, key)}
                >
                  {value}
                </ToggleButton>
              ))}
            </Col>
          </Row>
        ))}

        <Row>
          <Col lg={7}>
            <h6 className="ia-table" style={{ marginLeft: '50px' }}>
              6. Assessor comments
            </h6>
          </Col>
        </Row>
        <Row>
          <Col style={{ marginLeft: '50px' }} lg={8}>
            <Form.Group controlId="comments">
              <Form.Control
                className="comments"
                as="textarea"
                rows={2}
                value={modalAssessment?.assessorComments}
                onChange={(e) => handleAssessmentChange(e, 'assessorComments')}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="ia-table mt-3">
          <Col lg={7}>
            <h6 className="ia-table" style={{ marginLeft: '50px' }}>
              7. Assessment outcome
            </h6>
          </Col>
          <Col>
            {['Pass', 'Fail'].map((value) => (
              <ToggleButton
                key={`outcome-${value}`}
                id={`outcome-${value}`}
                type="radio"
                variant="outline-primary"
                name="radio-outcome"
                value={value}
                style={
                  modalAssessment?.outcome === value
                    ? { backgroundColor: 'navy', color: 'white' }
                    : {}
                }
                checked={modalAssessment?.outcome === value}
                onChange={(e) => handleAssessmentChange(e, 'outcome')}
              >
                {value}
              </ToggleButton>
            ))}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <ButtonLoading text="Cancel" onClick={() => setShowModal(false)} />
        <ButtonLoading
          text="Save"
          onClick={saveChartAssessment}
          disabled={chartButtonText === 'View Completed Assessment' || modalMode === 'view'}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default ChartAssessment;
